import getConfig from 'next/config';

import type { Hotel } from '@/interfaces';
import { StarRating } from '@/components/starRating/StarRating';
import type { SearchResultsItemProps } from '../searchResultsItem/SearchResultsItem';
import { extractNameFromKey } from '@/utils/kayakKeys';
import { SearchResultItemGuestRating } from '../searchResultItemGuestRating/SearchResultItemGuestRating';
import { trackMapClick, trackShowInfoAndOffer } from '@/utils/tracking/finnAnalytics';
import { updateSearchResultsPageQueryParams } from '@/utils/url/routerHelpers';
import { filtersToHotelPageBrowserQueryParams } from '@/utils/url/hotelPageQueryParams';

const { publicRuntimeConfig } = getConfig();

export type SearchResultItemDetailsProps = { hotelKey: string } & Pick<
    Hotel,
    'id' | 'name' | 'starRating' | 'guestRating' | 'numberOfReviews'
> &
    Pick<SearchResultsItemProps, 'setMapState' | 'mapOpen' | 'filters' | 'searchConfig'>;

export const SearchResultItemDetails = ({
    id,
    hotelKey,
    name,
    starRating,
    guestRating,
    numberOfReviews,
    setMapState,
    mapOpen,
    filters,
    searchConfig,
}: SearchResultItemDetailsProps) => {
    const cleanHotelKey = extractNameFromKey(hotelKey);
    const mapButtonClick = (event): void => {
        event.preventDefault();
        event.stopPropagation();
        trackMapClick(true);
        setMapState({ open: true, selectedHotel: { key: cleanHotelKey, centerOnHotel: true } });
        updateSearchResultsPageQueryParams(true, filters, searchConfig);
    };
    const hotelUrl = `${publicRuntimeConfig.basePath}/detaljer/${cleanHotelKey}/?${filtersToHotelPageBrowserQueryParams(filters)}`;

    return (
        <div className="result-list-item-details">
            <div>
                <StarRating id={id.toString()} rating={starRating} extraSpacing />
                <h3 className="u-t3--gt300 u-t4--lt300 two-line-ellipsis">
                    <a
                        data-testid="searchResultsItemLink"
                        className="link link--dark link--block link-covering-parent"
                        href={hotelUrl}
                        onClick={() => trackShowInfoAndOffer(id, name)}>
                        {name}
                    </a>
                </h3>
                <SearchResultItemGuestRating guestRating={guestRating} numberOfReviews={numberOfReviews} />
            </div>
            <div>
                <div className="result-list-item-more-info">
                    <button
                        type="button"
                        data-testid="map-button"
                        className="button button--link button--no-break u-caption"
                        style={{ zIndex: 2 }}
                        onClick={mapButtonClick}
                        aria-expanded={mapOpen}
                        aria-controls="result-list-map">
                        Vis i kart
                    </button>
                    <a
                        href={hotelUrl}
                        data-testid="searchResultsItemShowDetails"
                        onClick={() => trackShowInfoAndOffer(id, name)}
                        className="u-caption u-strong"
                        style={{ marginTop: '2px', marginLeft: '1px', zIndex: 2 }}>
                        <span>Mer info og alle tilbud</span>
                        <i className="icon icon--small icon--chevron-right-blue u-float-right u-ml8" />
                    </a>
                </div>
            </div>
        </div>
    );
};
